import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  memo
} from 'react';
import styled, { keyframes } from 'styled-components';
import Button from './Button.jsx';
import RequirementDetailsDialog from './RequirementDetailsDialog.jsx';
import { listRequirements, listStorageLinks } from '../graphql/queries.js';
import {
  createRequirement,
  updateRequirement,
  deleteRequirement,
  deleteStorageLink,
  createStorageLink
} from '../graphql/mutations.js';
import { message } from 'antd';
import RequirementsTour from './RequirementsTour.jsx';
import { useFlexibleTranslation } from '../contexts/LanguageContext.js';
import { getTranslatedTitleFromHierarchy } from '../utils/requirementTermTranslation.js';
import { graphqlRequest } from '../services/graphqlClient.js';
import ClassifierModal from './ClassifierModal.jsx';
import md5 from 'md5';
import axios from 'axios';
import { useAuth, getSignInProvider } from '../auth.js';
import LinkedFoldersSection from './LinkedFoldersSection.jsx';
import { useNotificationsContext } from '../contexts/NotificationsContext.js';
import ProjectService from '../services/projectService';
import { phFolderLinked, phFolderLinkFailed, phTourFolderLinked } from '../utils/posthogEvents';
import {
  folderUploadErrorMessage,
  isSharePointSiteNotGrantedError,
  resolveSharePointAccessTokenForUpload,
} from '../utils/sharepointFolderUpload.js';
import { isTeamSharePointUrl } from '../utils/sharepointSiteUrl.js';

const gridFloat = keyframes`
  0% { transform: translate(0, 0); }
  100% { transform: translate(-40px, -40px); }
`;

const PageContainer = styled.div`
  position: relative;
  height: 100%;
  overflow-y: auto;
  padding: 0 0 40px 0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const BackgroundGrid = styled.div`
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 107, 53, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 107, 53, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  animation: ${gridFloat} 20s linear infinite;
  pointer-events: none;
  z-index: 1;
`;


const RequirementCard = styled.div.attrs(() => ({
  className: 'requirement-card'
}))`
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: background 0.18s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fff7f1; /* soft orange tint like the design */
  }
`;

const RequirementMain = styled.div`
  padding: 14px 20px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #ffffff;
`;

const RequirementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const RequirementTitleSection = styled.div`
  flex: 1;
  padding-right: 4px;
`;

const RequirementTitle = styled.div`
  font-family: Montserrat, system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', sans-serif;
  font-weight: 700;
  color: #111827;
  font-size: 15px;
  margin-bottom: 2px;
  line-height: 1.4;
`;

const RequirementPath = styled.div`
  font-family: Montserrat, system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', sans-serif;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.3;
`;

const RequirementDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
`;


const MetaRight = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const LinkIconButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: #6b7280;
  transition: color 0.18s ease, transform 0.18s ease;

  &:hover {
    color: #f97316;
    transform: translateY(-1px);
  }
`;


const ExpandToggle = styled.button`
  border: none;
  background: transparent;
  padding: 0 2px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  color: #9ca3af;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #e97024;
    transform: translateY(-1px);
  }
`;

const LinksList = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 11px;
  color: #4b5563;
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  align-items: center;

  span {
    font-weight: 500;
  }

  a {
    font-size: 11px;
    text-decoration: underline;
    word-break: break-all;
    color: #e97024;
    font-weight: 600;
  }
`;


const GroupContainer = styled.div`
  margin-bottom: 16px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  position: relative;
  transition: border-color 0.25s ease, box-shadow 0.25s ease,
    transform 0.25s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #ff8a3c, #e97024);
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover {
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
    border-color: rgba(233, 112, 36, 0.7);
    transform: translateY(-2px);
  }

  &:hover::before {
    opacity: 1;
  }
`;

const GroupHeader = styled.button.attrs(() => ({
  className: 'requirements-group-header'
}))`

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: #ffffff;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
  outline: none;
`;

const GroupHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 16px;
  color: #111827;
  font-family: Montserrat, system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', sans-serif;
`;

const GroupToggleIcon = styled.span`
  font-size: 14px;
  color: #9ca3af;
`;

const GroupTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const GroupCount = styled.span`
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(233, 112, 36, 0.15);
  color: #111;
`;

const GroupCategoryContainer = styled.div`
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 4px;
`;

const GroupRequirements = styled.div`
  display: flex;
  flex-direction: column;
`;

const FolderInputSection = styled.div`
  padding: 2.5rem 24px 0;
  margin-bottom: 2.5rem;
  max-width: 720px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const StepLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #999999;
  margin-bottom: 0.5rem;
`;

const UrlInputGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const UrlInputWrap = styled.div`
  flex: 1;
  position: relative;
`;

const UrlInputIcon = styled.svg`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999999;
  width: 16px;
  height: 16px;
  pointer-events: none;
`;

const FolderInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.625rem;
  border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: #fafafa;
  font-size: 0.9rem;
  color: #333333;
  transition: all 0.2s;
  font-family: inherit;
  outline: none;

  &:focus {
    background: #ffffff;
    border-color: #e97024;
  }

  &::placeholder {
    color: #aaaaaa;
  }
`;

const InputHint = styled.div`
  font-size: 0.78rem;
  color: #999999;
  margin-top: 0.4rem;
`;

const LinkFolderBtn = styled.button`
  padding: 0.75rem 1.25rem;
  background: #e97024;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(233, 112, 36, 0.25);

  &:hover:not(:disabled) {
    background: #000000;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(233, 112, 36, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FolderInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(20px);
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.04);
`;

const RequirementsPage = memo(
  ({ selectedProject, isFirstVisit, projects, onOpenDrawingReview }) => {
    const { t } = useFlexibleTranslation();

    const [columns, setColumns] = useState([
      { id: 'design', title: 'Design', requirements: [] },
      { id: 'build', title: 'Build', requirements: [] },
      { id: 'operate', title: 'Operate', requirements: [] }
    ]);

    const [selectedRequirement, setSelectedRequirement] = useState(null);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
    const [isLinksDialogOpen, setIsLinksDialogOpen] = useState(false);
    const [selectedRequirementForLinks, setSelectedRequirementForLinks] =
      useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [showOnlyWithLinks, setShowOnlyWithLinks] = useState(false);
    const [requirementIdsWithLinks, setRequirementIdsWithLinks] = useState(
      new Set()
    );
    const [loadingMessage, setLoadingMessage] = useState('');
    const [loadingProgress, setLoadingProgress] = useState(0);

    const mountedRef = useRef(false);
    const loadingRef = useRef(false);
    const loadedRef = useRef(false);
    const initialLoadRef = useRef(true);
    const loadPromiseRef = useRef(null);
    const abortControllerRef = useRef(null);
    const loadAttemptedRef = useRef(false);
    const mountTimeoutRef = useRef(null);

    const [selectedProjectState, setSelectedProjectState] = useState(null);

    const [expandedCards, setExpandedCards] = useState(new Set());
    const [cardLinks, setCardLinks] = useState({});
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [newRequirementColumnId, setNewRequirementColumnId] = useState(null);
    const auth = useAuth();
    const { addNotification, addToast, updateNotificationById, resumePollingForJob, notifications } = useNotificationsContext();
    const [classifierModalOpen, setClassifierModalOpen] = useState(false);
    const [classifierJobId, setClassifierJobId]         = useState('');
    const [classifierFolderName, setClassifierFolderName] = useState('');
    const [classifierTotalFiles, setClassifierTotalFiles] = useState(0);
    const [pendingFolderUrl, setPendingFolderUrl]         = useState('');
    const [activeClassifierFolderUrl, setActiveClassifierFolderUrl] = useState('');
    const [pendingRequirementId, setPendingRequirementId] = useState('');
    const [pendingLinkId, setPendingLinkId] = useState('');

    const getIndentLevel = (hierarchy = {}) => {
      const levels = [
        hierarchy.mainCategory,
        hierarchy.subCategory,
        hierarchy.subSubCategory,
        hierarchy.level4,
        hierarchy.level5,
        hierarchy.level6,
        hierarchy.level7
      ];
      for (let i = levels.length - 1; i >= 0; i--) {
        if (levels[i]) return i;
      }
      return 0;
    };

    const getTitleFromHierarchy = (hierarchy = {}) => {
      return getTranslatedTitleFromHierarchy(hierarchy, t);
    };

    const findRequirementByPhaseIndustryCategory = useCallback(
      (status, industry, category) => {
        const phaseId = (status || 'design').toLowerCase();
        const col = columns.find(c => c.id === phaseId);
        if (!col) return null;

        const found = col.requirements.find(req => {
          const m = req.metadata || {};
          return m.industry === industry && m.category === category;
        });

        return found;
      },
      [columns]
    );

    const getProjectId = useCallback(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const fromUrl = urlParams.get('projectId');
      const fromSelectedState = selectedProjectState?.id;
      const fromSelectedProp = selectedProject?.id;

      const projectId = fromUrl || fromSelectedState || fromSelectedProp;

      return projectId;
    }, [selectedProjectState, selectedProject]);

    useEffect(() => {
      if (selectedProject) {
        setSelectedProjectState(selectedProject);
      }
    }, [selectedProject]);

    const safeSetState = useCallback((setter, value) => {
      if (mountedRef.current) {
        setter(value);
      } else {
      }
    }, []);
    const ensureProjectSelected = useCallback(() => {
      const projectId = getProjectId();

      if (!projectId) {
        message.warning(
          t('requirements.selectProjectFirst', 'Please select a project first.')
        );
        return null;
      }
      return projectId;
    }, [getProjectId, t]);


    const storageLinkCacheRef = useRef(new Map());
    const pendingStorageLinkQueriesRef = useRef(new Map());
    const classifierConfirmedRef = useRef(false);
    const linkedFoldersAddFolderRef = useRef(null);

    const clearStorageLinkCache = useCallback(requirementId => {
      if (requirementId) {
        storageLinkCacheRef.current.delete(requirementId);
      } else {
        storageLinkCacheRef.current.clear();
      }
    }, []);

    const fetchStorageLinksWithCache = useCallback(async requirementId => {
      if (!requirementId) {
        console.warn(
          ': fetchStorageLinksWithCache called without requirementId'
        );
        return [];
      }

      if (storageLinkCacheRef.current.has(requirementId)) {
        return storageLinkCacheRef.current.get(requirementId);
      }

      if (pendingStorageLinkQueriesRef.current.has(requirementId)) {
        return pendingStorageLinkQueriesRef.current.get(requirementId);
      }

      const queryPromise = (async () => {
        try {
          const response = await graphqlRequest({
            query: listStorageLinks,
            variables: {
              filter: {
                requirementId: { eq: requirementId }
              },
              limit: 1000
            }
          });

          const links = response.data?.listStorageLinks?.items || [];
          storageLinkCacheRef.current.set(requirementId, links);
          return links;
        } catch (error) {
          console.error(
            ': Error fetching storage links:',
            requirementId,
            error
          );
          return [];
        } finally {
          pendingStorageLinkQueriesRef.current.delete(requirementId);
        }
      })();

      pendingStorageLinkQueriesRef.current.set(requirementId, queryPromise);
      return queryPromise;
    }, []);

    const toggleCardExpanded = async requirementId => {

      setExpandedCards(prev => {
        const next = new Set(prev);
        if (next.has(requirementId)) {
          next.delete(requirementId);
        } else {
          next.add(requirementId);
        }
        return next;
      });

      if (!cardLinks[requirementId]) {
        try {
          const links = await fetchStorageLinksWithCache(requirementId);
          setCardLinks(prev => ({
            ...prev,
            [requirementId]: links || []
          }));
        } catch (err) {
          console.error(
            ': Error loading links for card',
            requirementId,
            err
          );
        }
      } else {
      }
    };

    const toggleGroupExpanded = useCallback(groupKey => {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        if (next.has(groupKey)) {
          next.delete(groupKey);
        } else {
          next.add(groupKey);
        }
        return next;
      });
    }, []);

    const loadRequirements = useCallback(async () => {
      const projectId = getProjectId();
      if (!projectId) {
        return;
      }

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      if (loadingRef.current) {
        return;
      }

      loadAttemptedRef.current = true;
      loadingRef.current = true;
      loadedRef.current = false;
      initialLoadRef.current = false;

      loadPromiseRef.current = (async () => {
        try {
          safeSetState(
            setLoadingMessage,
            t('requirements.loadingRequirements', 'Loading requirements...')
          );
          safeSetState(setLoadingProgress, 10);

          let allRequirements = [];
          let nextToken = null;
          let page = 0;

          do {
            const backendRes = await graphqlRequest({
              query: listRequirements,
              variables: {
                filter: { projectId: { eq: projectId } },
                limit: 1000,
                nextToken
              }
            });

            const {
              items,
              nextToken: newNextToken
            } = backendRes.data?.listRequirements || {};

            if (items) allRequirements = allRequirements.concat(items);
            nextToken = newNextToken;
            page += 1;
          } while (nextToken && !signal.aborted);

          if (signal.aborted) {
            return;
          }

          const newColumns = [
            { id: 'design', title: 'Design', requirements: [] },
            { id: 'build', title: 'Build', requirements: [] },
            { id: 'operate', title: 'Operate', requirements: [] }
          ];

          allRequirements.forEach(req => {
            try {
              let metadata = {};
              let parsed = false;

              if (typeof req.description === 'string') {
                try {
                  const parsedDesc = JSON.parse(req.description);
                  if (
                    parsedDesc &&
                    typeof parsedDesc === 'object' &&
                    !Array.isArray(parsedDesc)
                  ) {
                    metadata = parsedDesc;
                    parsed = true;
                  }
                } catch (e) {
                  console.warn(
                    ': Failed to parse description JSON, treating as legacy text',
                    req.id
                  );
                }
              }

              const status = (
                metadata.status ||
                req.status ||
                'design'
              ).toLowerCase();
              const column = newColumns.find(c => c.id === status);

              if (!column) {
                console.warn(
                  ': requirement has unknown status, skipping',
                  req.id,
                  status
                );
                return;
              }

              const indentLevel = getIndentLevel(metadata?.hierarchy);

              const isV2Requirement =
                !!metadata.industry && !!metadata.category;

              const title = isV2Requirement
                ? getTitleFromHierarchy(metadata?.hierarchy) || req.title
                : req.title;

              const description = isV2Requirement
                ? metadata.text || ''
                : (parsed ? '' : req.description || '');

              column.requirements.push({
                ...req,
                metadata,
                indentLevel,
                title,
                description
              });
            } catch (err) {
              console.error(': Error processing requirement', req, err);
            }
          });

          safeSetState(setColumns, newColumns);
          safeSetState(setLoadingProgress, 100);
          safeSetState(setLoadingMessage, '');
          loadedRef.current = true;
          loadingRef.current = false;

          setExpandedGroups(new Set());
        } catch (error) {
          console.error('Error loading requirements:', error);
          safeSetState(
            setLoadingMessage,
            t(
              'requirements.errorLoadingRequirements',
              'Error loading requirements. Please try again.'
            )
          );
        } finally {
          loadingRef.current = false;
          loadedRef.current = true;
          loadPromiseRef.current = null;
        }
      })();

      return loadPromiseRef.current;
    }, [getProjectId, safeSetState, t]);

    useEffect(() => {
      mountedRef.current = true;

      if (mountTimeoutRef.current) {
        clearTimeout(mountTimeoutRef.current);
      }

      mountTimeoutRef.current = setTimeout(() => {
        if (!loadAttemptedRef.current) {
          loadRequirements();
        }
      }, 300);

      return () => {
        if (mountTimeoutRef.current) {
          clearTimeout(mountTimeoutRef.current);
        }
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        mountedRef.current = false;
      };
    }, [loadRequirements]);

    useEffect(() => {
      const fetchAllStorageLinks = async () => {
        if (!selectedProject?.id) {
          return;
        }
        try {
          let allLinks = [];
          let nextToken = null;
          let page = 0;
          do {
            const res = await graphqlRequest({
              query: listStorageLinks,
              variables: {
                filter: { projectId: { eq: selectedProject.id } },
                limit: 1000,
                nextToken
              }
            });
            const {
              items,
              nextToken: newNextToken
            } = res.data?.listStorageLinks || {};          

            if (items) allLinks = allLinks.concat(items);
            nextToken = newNextToken;
            page += 1;
          } while (nextToken);

          const idSet = new Set(allLinks.map(link => link.requirementId));
          setRequirementIdsWithLinks(idSet);
        } catch (e) {
          console.error(': Error fetching all storage links:', e);
          setRequirementIdsWithLinks(new Set());
        }
      };
      fetchAllStorageLinks();
    }, [selectedProject]);

    const handleFolderUrlSubmit = async (folderUrl) => {
      if (!selectedProject?.id) {
        message.warning('Please select a project first.');
        return;
      }

      const trimmed = folderUrl.trim();
      if (!trimmed) return;

      const userId    = auth.user?.sub || 'unknown';
      const userEmail = String(
        auth.user?.profile?.email || auth.user?.email || 'unknown'
      ).trim().toLowerCase();

      const requirementId = `classifier-${Date.now()}`;
      const linkId  = md5(`${trimmed}_${selectedProject.id}_${requirementId}`);
      const jobId   = `${selectedProject.id}_${requirementId}_${linkId}`;
      setPendingRequirementId(requirementId);
      setPendingLinkId(linkId);

      const UPLOAD_BASE = process.env.REACT_APP_BACKEND_URL_UPLOAD || 'http://localhost:8000';
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

      try {
        const idToken = localStorage.getItem('id_token');
        const isSharePoint = trimmed.includes('sharepoint.com') || trimmed.includes('onedrive.live.com') || trimmed.includes('1drv.ms');
        const isGoogleDriveUrl = trimmed.includes('drive.google.com');
        const isGovPortalAppian = trimmed.includes('texas-sos.appianportalsgov.com');
        const isGovPortalTxStatutes = trimmed.includes('statutes.capitol.texas.gov');
        const isGovPortal = isGovPortalAppian || isGovPortalTxStatutes;
        const signInProvider = getSignInProvider(idToken);
        if (signInProvider === 'google' && isSharePoint) {
          const msg = "You're signed in with Google, so this account can't connect SharePoint or OneDrive folders. Sign in with Microsoft to link this folder, or paste a Google Drive folder URL instead.";
          message.error(msg);
          phFolderLinkFailed({ projectId: selectedProject.id, source: 'sharepoint', reason: 'provider_mismatch', errorMessage: msg });
          return;
        }
        if (signInProvider === 'microsoft' && isGoogleDriveUrl) {
          const msg = "You're signed in with Microsoft, so this account can't connect Google Drive folders. Sign in with Google to link this folder, or paste a SharePoint/OneDrive folder URL instead.";
          message.error(msg);
          phFolderLinkFailed({ projectId: selectedProject.id, source: 'gdrive', reason: 'provider_mismatch', errorMessage: msg });
          return;
        }

        let googleAccessToken;
        let sharepointAccessToken;

        if (isSharePoint) {
          sharepointAccessToken = await resolveSharePointAccessTokenForUpload(trimmed, idToken);
        } else {
          const res = await fetch(`${API_BASE}/api/google/drive/token`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${idToken}` },
          });
          const data = await res.json();
          googleAccessToken = data.access_token;
        }

        const uploadRes = await axios.post(
          `${UPLOAD_BASE}/uploadFromDrive`,
          {
            folderUrl:     trimmed,
            requirementId,
            projectId:     selectedProject.id,
            companyId:     selectedProject.companyId,
            linkId,
            userId,
            userEmail,
            googleAccessToken,
            sharepointAccessToken,
          },
          { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
        );

        const uploadBody = typeof uploadRes.data === 'string'
          ? JSON.parse(uploadRes.data)
          : uploadRes.data;

        const urlParts = trimmed.replace(/\?.*$/, '').split('/');
        const lastUrlSegment = urlParts[urlParts.length - 1] || '';
        // Some share-link formats put the real folder path in a query param instead of the
        // URL path — Google's "open?id=<fileId>" and SharePoint's classic
        // ".../Forms/AllItems.aspx?id=<server-relative-path>". SharePoint's id param is a real
        // human-readable path, so pull the folder name out of it when present.
        let queryFolderName = null;
        try {
          // Extract 'id'/'RootFolder' from the raw query string rather than via
          // URLSearchParams -- URLSearchParams decodes '+' as a literal space
          // (application/x-www-form-urlencoded semantics), which corrupts
          // SharePoint's base64-ish sharing IDs whenever they contain a '+'
          // (they're not form-encoded, so '+' there means '+', not space).
          const queryString = trimmed.split('?')[1] || '';
          const rawParam = queryString
            .split('&')
            .map((pair) => pair.split('='))
            .find(([k]) => k === 'id' || k === 'RootFolder');
          const idParam = rawParam?.[1] ? decodeURIComponent(rawParam[1]) : null;
          const idSegments = idParam ? idParam.split('/').filter(Boolean) : [];
          if (idSegments.length > 1) queryFolderName = idSegments[idSegments.length - 1];
        } catch { /* not a parseable URL, fall through to path-based fallback */ }

        // Otherwise the last path segment is just a generic boilerplate word (e.g. "open",
        // SharePoint's "AllItems.aspx"/"Forms") rather than an actual folder name — show a
        // friendly label instead of that literal word.
        const GENERIC_URL_SEGMENTS = new Set([
          'open', 'folders', 'view', 'edit', 'd', 'u', '0', '',
          'allitems.aspx', 'default.aspx', 'forms',
        ]);
        const fallbackName = queryFolderName
          ? queryFolderName
          : isGovPortalAppian
          ? 'Texas TAC (Appian)'
          : isGovPortalTxStatutes
          ? 'Texas Statutes'
          : GENERIC_URL_SEGMENTS.has(lastUrlSegment.toLowerCase())
          ? (isSharePoint ? 'SharePoint Folder' : 'Google Drive Folder')
          : lastUrlSegment;
        const folderName = uploadBody?.folderName || fallbackName;

        phFolderLinked({
          projectId: selectedProject.id,
          source: isSharePoint ? 'sharepoint' : isGovPortal ? 'gov_portal' : 'gdrive',
        });

        const serverJobId = uploadBody?.jobId || jobId;
        const serverTotalFiles = Number(
          uploadBody?.totalFiles ?? uploadBody?.fileCount ?? uploadBody?.count ?? 0,
        ) || 0;

        setClassifierJobId(serverJobId);
        setClassifierFolderName(folderName);
        setClassifierTotalFiles(serverTotalFiles);
        setActiveClassifierFolderUrl(trimmed);
        setClassifierModalOpen(true);
      } catch (err) {
        console.error('[RequirementsPage] handleFolderUrlSubmit error:', err);
        const msg = folderUploadErrorMessage(err);
        message.error(msg);
        phFolderLinkFailed({
          projectId: selectedProject.id,
          source: isTeamSharePointUrl(trimmed) ? 'sharepoint' : null,
          reason: isSharePointSiteNotGrantedError(err) ? 'site_not_granted' : 'unknown',
          errorMessage: msg,
        });
      }
    };

    const handleClassifierConfirmed = async (confirmedRows, jobId) => {
      classifierConfirmedRef.current = true;
      phTourFolderLinked();
      setPendingRequirementId('');
      setPendingLinkId('');
      const unknownCount = confirmedRows.filter(r => r.category === 'Unknown').length;
      const total = confirmedRows.length;

      const folderName = classifierFolderName || 'Linked Folder';

      const reqCount = confirmedRows.filter(r => r.category === 'Requirement').length;
      const delCount = confirmedRows.filter(r => r.category !== 'Requirement').length;

      const projectId = selectedProject?.id;

      const projectIdLength = projectId.length;
      const afterProjectId = jobId.slice(projectIdLength + 1); 
      const underscoreIdx = afterProjectId.indexOf('_');
      const requirementId = afterProjectId.slice(0, underscoreIdx);
      const linkId = afterProjectId.slice(underscoreIdx + 1);

      const descriptionMetadata = {
        status:    'design',
        industry:  'AECO',
        category:  'Classifier Upload',
        text:      `Auto-created from classifier upload. ${reqCount} requirement docs, ${delCount} deliverable docs.`,
        storage:   [{
          id:   linkId,
          url:  activeClassifierFolderUrl,
          name: folderName,
          type: 'google',
        }],
        files:     confirmedRows.map(r => ({
          fileKey:  r.fileKey,
          fileName: r.fileName || r.fileKey?.split('/').pop() || r.fileKey,
          category: r.category,
          docClass: r.docClass || '',
          structuralMode: r.structuralMode || (r.docClass === 'drawing' ? 'drawing_annotation' : ''),
          complianceType: r.complianceType || '',
        })),
        source:    'classifier',
        jobId,
        hierarchy: {
          mainCategory: 'AECO',
          subCategory:  'Classifier Upload',
        }
      };

      try {
        // 1) Create the Requirement using existing GraphQL mutation
        await graphqlRequest({
          query: createRequirement,
          variables: {
            input: {
              id:          requirementId,
              projectId,
              title:       folderName,
              description: JSON.stringify(descriptionMetadata),
            }
          }
        });

        // 2) Create the StorageLink using existing GraphQL mutation
        await graphqlRequest({
          query: createStorageLink,
          variables: {
            input: {
              id:            linkId,
              name:          folderName,
              type:          'google',
              url:           activeClassifierFolderUrl,
              projectId,
              requirementId,
            }
          }
        });

      } catch (err) {
        console.warn('[RequirementsPage] Failed to create requirement/storage link:', err);
      }
      const newReq = {
        id: requirementId,
        projectId,
        title: folderName,
        metadata: descriptionMetadata,
        indentLevel: 0,
        description: descriptionMetadata.text,
      };

      setColumns(prev => prev.map(col =>
        col.id === 'design'
          ? { ...col, requirements: [...col.requirements, newReq] }
          : col
      ));

      setRequirementIdsWithLinks(prev => {
        const next = new Set(prev);
        next.add(requirementId);
        return next;
      });
      // If any confirmed row is a BUILDING_CODE requirement, flag the project
      const hasBuildingCodeDoc = confirmedRows.some(
        r => r.category === 'Requirement' && r.complianceType === 'BUILDING_CODE'
      );
      if (hasBuildingCodeDoc && selectedProject?.id) {
        try {
          await ProjectService.updateProject(selectedProject.id, {
            hasBuildingCodeExtractions: true,
          });
          setSelectedProjectState(prev => prev
            ? { ...prev, hasBuildingCodeExtractions: true }
            : prev
          );
        } catch (err) {
          console.warn('[RequirementsPage] Failed to set hasBuildingCodeExtractions:', err);
        }
      }

      const pendingNotif = await addNotification({
        type: 'info',
        title: 'Indexing started',
        message: `${total} document${total !== 1 ? 's' : ''} are being indexed in the background.`,
        jobId,
      });

      addToast({
        type: 'info',
        title: 'Indexing started',
        message: `${total} document${total !== 1 ? 's' : ''} are being indexed in the background.`,
      });

      if (unknownCount > 0) {
        addToast({
          type: 'warning',
          title: `${unknownCount} document${unknownCount > 1 ? 's' : ''} had low confidence`,
          message: `They've been marked as Deliverable by default.`,
        });
        await addNotification({
          type: 'warning',
          title: `${unknownCount} document${unknownCount > 1 ? 's' : ''} had low confidence`,
          message: `They've been marked as Deliverable by default.`,
        });
      }

      if (pendingNotif?.notificationId) {
        resumePollingForJob({
          jobId,
          notificationId: pendingNotif.notificationId,
          projectId: selectedProject.id,
          linkId,
          meta: {
            folderName,
            documents: confirmedRows.map(r => ({
              fileName: r.fileName || r.fileKey?.split('/').pop() || r.fileKey,
              category: r.category,
              docClass: r.docClass || '',
            })),
            isDeliverableOnly: reqCount === 0,
            hasDrawingDeliverable: confirmedRows.some(
              r => r.category !== 'Requirement' && r.docClass === 'drawing'
            ),
            startedAt: Date.now(),
          },
        });
      }
      if (linkedFoldersAddFolderRef.current) {
        const folderFiles = confirmedRows.map(r => ({
          fileKey: r.fileKey,
          fileName: r.fileName || r.fileKey?.split('/').pop() || r.fileKey,
          category: r.category,
        }));
        linkedFoldersAddFolderRef.current({
          requirementId,
          title: folderName,
          folderUrl: activeClassifierFolderUrl,
          linkId,
          jobId,
          type: activeClassifierFolderUrl.includes('sharepoint.com') ? 'sharepoint' : 'google',
          isLegacy: false,
        }, folderFiles);
      }
    };
    const handleConstructionTypeConfirmed = async (constructionType) => {
      if (!selectedProject?.id || !constructionType) return;
      try {
        await ProjectService.updateProject(selectedProject.id, {
          constructionType,
        });
        if (selectedProjectState) {
          setSelectedProjectState(prev => ({ ...prev, constructionType }));
        }
      } catch (err) {
        console.error('[RequirementsPage] Failed to save construction type:', err);
        message.error('Failed to save construction type. Indexing will proceed unfiltered.');
      }
    };

    const handleZoningDesignationConfirmed = async (zoningDesignation) => {
      if (!selectedProject?.id || !zoningDesignation) return;
      try {
        await ProjectService.updateProject(selectedProject.id, {
          zoningDesignation,
        });
        if (selectedProjectState) {
          setSelectedProjectState(prev => ({ ...prev, zoningDesignation }));
        }
      } catch (err) {
        console.error('[RequirementsPage] Failed to save zoning designation:', err);
        message.error('Failed to save zoning designation. Indexing will proceed unfiltered.');
      }
    };

    const handleEditRequirement = async requirement => {
      const projectId = ensureProjectSelected();
      if (!projectId) return;
      setNewRequirementColumnId(null);

      const backendId = requirement.backendId || requirement.id;

      if (!backendId) {
        setSelectedRequirement({
          ...requirement,
          backendId: null,
          storageLinks: [],
          metadata: {}
        });
        setIsDetailsDialogOpen(true);
        return;
      }

      const storageLinks = await fetchStorageLinksWithCache(backendId);
      const metadata = requirement.metadata || {};
      const descriptionText =
        metadata.text || requirement.description || '';

      const titleFromHierarchy =
        metadata.hierarchy?.level7 ||
        metadata.hierarchy?.level6 ||
        metadata.hierarchy?.level5 ||
        metadata.hierarchy?.level4 ||
        metadata.hierarchy?.subSub ||
        requirement.title;

      const requirementData = {
        ...requirement,
        id: backendId,
        backendId,
        projectId: selectedProject.id,
        storageLinks,
        title: titleFromHierarchy || requirement.title,
        description: descriptionText,
        metadata
      };

      setSelectedRequirement(requirementData);
      setIsDetailsDialogOpen(true);
    };

    const handleSaveRequirement = async requirementData => {
      const projectId = selectedProject.id;
      const targetStatus = (
        requirementData.status ||
        newRequirementColumnId ||
        'design'
      ).toLowerCase();

      const { industry, category } = requirementData;

      if (!industry || !category) {
        message.error('Industry and category are required.');
        console.warn(
          ': handleSaveRequirement - missing industry/category',
          requirementData
        );
        return;
      }

      if (selectedRequirement) {
        clearStorageLinkCache(selectedRequirement.id);

        try {
          const previousMetadata = selectedRequirement?.metadata || {};
          const descriptionMetadata = {
            ...previousMetadata,
            text: requirementData.description || previousMetadata.text,
            status: targetStatus,
            parameters:
              requirementData.parameters ??
              previousMetadata.parameters ??
              [],
            hierarchy:
              requirementData.hierarchy ??
              previousMetadata.hierarchy ??
              {},
            storage:
              requirementData.storage ??
              previousMetadata.storage ??
              null,
            industry,
            category
          };

          const response = await graphqlRequest({
            query: updateRequirement,
            variables: {
              input: {
                id: selectedRequirement.id,
                title: requirementData.title,
                description: JSON.stringify(descriptionMetadata)
              }
            }
          });

          const updatedReq = response.data?.updateRequirement;

          if (!updatedReq) {
            message.error('Failed to update requirement');
            setIsDetailsDialogOpen(false);
            return;
          }

          let metadata = descriptionMetadata;
          try {
            if (typeof updatedReq.description === 'string') {
              metadata = JSON.parse(updatedReq.description);
            }
          } catch (e) {
            console.warn(
              ': Failed to parse updatedReq.description, using descriptionMetadata',
              updatedReq.description
            );
          }

          const indentLevel = getIndentLevel(metadata?.hierarchy);

          setColumns(prevColumns =>
            prevColumns.map(col => ({
              ...col,
              requirements: col.requirements.map(req =>
                req.id === selectedRequirement.id
                  ? {
                      ...req,
                      ...updatedReq,
                      metadata,
                      indentLevel,
                      title:
                        getTitleFromHierarchy(metadata?.hierarchy) ||
                        updatedReq.title,
                      description: metadata.text || ''
                    }
                  : req
              )
            }))
          );
          setIsDetailsDialogOpen(false);

          return {
            ...updatedReq,
            projectId,
            metadata,
            title:
              getTitleFromHierarchy(metadata?.hierarchy) ||
              updatedReq.title,
            description: metadata.text || ''
          };
        } catch (error) {
          console.error(
            ': Error updating requirement in AWS:',
            error
          );
          message.error('Failed to update requirement');
          setIsDetailsDialogOpen(false);
          return;
        }
      }

      const previousMetadata = {};
      const descriptionMetadata = {
        ...previousMetadata,
        text: requirementData.description || '',
        status: targetStatus,
        parameters: requirementData.parameters ?? [],
        hierarchy: requirementData.hierarchy ?? {},
        storage: requirementData.storage ?? null,
        industry,
        category
      };

      const existing = findRequirementByPhaseIndustryCategory(
        targetStatus,
        industry,
        category
      );

      if (existing) {
        try {
          const response = await graphqlRequest({
            query: updateRequirement,
            variables: {
              input: {
                id: existing.id,
                title: requirementData.title,
                description: JSON.stringify(descriptionMetadata)
              }
            }
          });

          const updated = response.data?.updateRequirement;

          if (!updated) {
            message.error('Failed to update existing requirement');
            setIsDetailsDialogOpen(false);
            return;
          }

          let metadata = descriptionMetadata;
          try {
            if (typeof updated.description === 'string') {
              metadata = JSON.parse(updated.description);
            }
          } catch (e) {
            console.warn(
              ': Failed to parse updated.description, using descriptionMetadata',
              updated.description
            );
          }

          const indentLevel = getIndentLevel(metadata?.hierarchy);

          setColumns(prev =>
            prev.map(col =>
              col.id === targetStatus
                ? {
                    ...col,
                    requirements: col.requirements.map(req =>
                      req.id === existing.id
                        ? {
                            ...req,
                            ...updated,
                            metadata,
                            indentLevel,
                            title:
                              getTitleFromHierarchy(metadata?.hierarchy) ||
                              updated.title,
                            description: metadata.text || ''
                          }
                        : req
                    )
                  }
                : col
            )
          );
          setIsDetailsDialogOpen(false);

          return {
            ...updated,
            projectId,
            metadata,
            title:
              getTitleFromHierarchy(metadata?.hierarchy) ||
              updated.title,
            description: metadata.text || ''
          };
        } catch (err) {
          console.error(': Error updating existing card:', err);
          message.error('Failed to reuse existing requirement');
          setIsDetailsDialogOpen(false);
          return;
        }
      }

      try {
        const response = await graphqlRequest({
          query: createRequirement,
          variables: {
            input: {
              projectId,
              title: requirementData.title,
              description: JSON.stringify(descriptionMetadata)
            }
          }
        });

        const created = response.data?.createRequirement;

        if (!created) {
          message.error('Failed to create requirement');
          setIsDetailsDialogOpen(false);
          return;
        }

        const metadata = descriptionMetadata;
        const indentLevel = getIndentLevel(metadata?.hierarchy);

        const newRequirement = {
          ...created,
          projectId,
          metadata,
          indentLevel,
          title: requirementData.title,
          description: metadata.text || ''
        };

        setColumns(prevColumns =>
          prevColumns.map(col =>
            col.id === targetStatus
              ? {
                  ...col,
                  requirements: [...col.requirements, newRequirement]
                }
              : col
          )
        );

        setIsDetailsDialogOpen(false);

        return newRequirement;
      } catch (error) {
        console.error(
          ': Error creating requirement in AWS:',
          error
        );
        message.error('Failed to create requirement');
        setIsDetailsDialogOpen(false);
        return;
      }
    };

    const handleDeleteRequirement = useCallback(
      async ({ requirementId, projectId, storageLinks }) => {
        if (!requirementId) {
          console.warn(
            'handleDeleteRequirement called without requirementId'
          );
          return;
        }
        try {
          let linksToDelete = Array.isArray(storageLinks)
            ? storageLinks
            : [];

          if (!linksToDelete.length) {
            linksToDelete = await fetchStorageLinksWithCache(requirementId);
          }

          if (linksToDelete && linksToDelete.length) {
            for (const link of linksToDelete) {
              if (!link?.id) continue;
              try {
                await graphqlRequest({
                  query: deleteStorageLink,
                  variables: { input: { id: link.id } }
                });
              } catch (err) {
                console.error(
                  'Failed to delete storageLink',
                  link.id,
                  err
                );
              }
            }
          }

          clearStorageLinkCache(requirementId);
          setRequirementIdsWithLinks(prev => {
            const next = new Set(prev);
            next.delete(requirementId);
            return next;
          });

          const resp = await graphqlRequest({
            query: deleteRequirement,
            variables: { input: { id: requirementId } }
          });

          setColumns(prevCols =>
            prevCols.map(col => ({
              ...col,
              requirements: col.requirements.filter(
                r => r.id !== requirementId
              )
            }))
          );

          setExpandedCards(prev => {
            const next = new Set(prev);
            next.delete(requirementId);
            return next;
          });
          setCardLinks(prev => {
            const next = { ...prev };
            delete next[requirementId];
            return next;
          });

          message.success('Requirement deleted.');
        } catch (err) {
          console.error('Error deleting requirement', err);
          message.error('Failed to delete requirement.');
        }
      },
      [clearStorageLinkCache, fetchStorageLinksWithCache]
    );

    const handleLinksChanged = useCallback(
      (requirementId, links) => {
        if (!requirementId) return;

        const hasLinksNow = Array.isArray(links) && links.length > 0;

        setRequirementIdsWithLinks(prev => {
          const next = new Set(prev);
          if (hasLinksNow) {
            next.add(requirementId);
          } else {
            next.delete(requirementId);
          }
          return next;
        });

        setCardLinks(prev => ({
          ...prev,
          [requirementId]: links || []
        }));

        clearStorageLinkCache(requirementId);
      },
      [clearStorageLinkCache]
    );

    const translatedColumns = useMemo(() => {
      const mapped = columns.map(column => ({
        ...column,
        title: t(`requirements.${column.id}`, column.title)
      }));
      return mapped;
    }, [columns, t]);

    const filteredColumns = useMemo(() => {
      const result = translatedColumns.map(column => {
        const filteredReqs = column.requirements.filter(req => {
          const matchesSearch =
            !searchQuery.trim() ||
            req.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            req.description?.toLowerCase().includes(searchQuery.toLowerCase());

          const isV2Requirement =
            !!req.metadata?.industry && !!req.metadata?.category;
          const hasLinks =
            requirementIdsWithLinks instanceof Set &&
            requirementIdsWithLinks.has(req.id);

          const allowedByLegacyRule = isV2Requirement ? true : hasLinks;

          const matchesLinksFilter = showOnlyWithLinks ? hasLinks : allowedByLegacyRule;

          return matchesSearch && matchesLinksFilter;
        });

        return {
          ...column,
          requirements: filteredReqs
        };
      });

      return result;
    }, [
      translatedColumns,
      searchQuery,
      showOnlyWithLinks,
      requirementIdsWithLinks
    ]);
    const requirementsCount = useMemo(() => {
      return columns.reduce((sum, c) => sum + (c.requirements?.length || 0), 0);
    }, [columns]);



    const buildHierarchyTree = useCallback((columnId, requirements = []) => {
      const rootMap = new Map();

      requirements.forEach(req => {
        const metadata = req.metadata || {};
        const h = metadata.hierarchy || {};

        const industry =
          metadata.industry || h.mainCategory || 'Other';
        const category =
          metadata.category || h.subCategory || 'Other';
        const level4 = h.subSubCategory || h.level4;
        const level5 = h.level5;
        const level6 = h.level6;
        const level7 = h.level7;

        const labels = [industry, category, level4, level5, level6, level7]
          .filter(label => label && String(label).trim() !== '');

        if (!labels.length) return;

        let currentMap = rootMap;
        let pathParts = [];
        let parentNode = null;

        labels.forEach((label, depth) => {
          pathParts.push(label);
          const pathStr = pathParts.join(' > ');
          const key = `col:${columnId}|lvl:${depth}|path:${pathStr}`;

          if (!currentMap.has(key)) {
            const node = {
              key,
              label,
              level: depth,
              childrenMap: new Map(),
              children: [],
              requirements: []
            };
            currentMap.set(key, node);
          }

          const node = currentMap.get(key);
          parentNode = node;
          currentMap = node.childrenMap;
        });

        if (parentNode) {
          parentNode.requirements.push(req);
        }
      });

      const convert = map => {
        return Array.from(map.values()).map(node => {
          const children = convert(node.childrenMap);
          return {
            key: node.key,
            label: node.label,
            level: node.level,
            children,
            requirements: node.requirements
          };
        });
      };

      return convert(rootMap);
    }, []);

    const renderRequirementCard = useCallback(
      req => {
        const metadata = req.metadata || {};

        const linksForCard = cardLinks[req.id];

        const hasBackendLinks =
          requirementIdsWithLinks instanceof Set &&
          requirementIdsWithLinks.has(req.id);

        const hasLocalLinks =
          Array.isArray(linksForCard) && linksForCard.length > 0;

        const hasLinks = hasBackendLinks || hasLocalLinks;

        const isExpanded =
          expandedCards instanceof Set &&
          expandedCards.has(req.id);

        const title =
          metadata.industry && metadata.category
            ? `${metadata.industry} — ${metadata.category}`
            : req.title;

        const phaseLabel = metadata.status
          ? `${metadata.status.charAt(0).toUpperCase()}${metadata.status
              .slice(1)
              .toLowerCase()}`
          : null;

        const pathParts = [];
        if (phaseLabel) pathParts.push(phaseLabel);
        if (metadata.industry) pathParts.push(metadata.industry);
        if (metadata.category) pathParts.push(metadata.category);
        const path =
          pathParts.length > 0 ? pathParts.join(' > ') : null;

        const shortDesc = (metadata.text || req.description || '').slice(
          0,
          180
        );

        return (
          <RequirementCard
            key={req.id}
            onClick={() => handleEditRequirement(req)}
          >
            <RequirementMain>
              <RequirementHeader>
                <RequirementTitleSection>
                  <RequirementTitle>{title}</RequirementTitle>
                  {path && <RequirementPath>{path}</RequirementPath>}
                </RequirementTitleSection>

                {hasLinks && (
                  <MetaRight>
                    <LinkIconButton
                      onClick={e => {
                        e.stopPropagation();
                        toggleCardExpanded(req.id);
                      }}
                      aria-label={isExpanded ? 'Show links' : 'Show links'}
                      title="View links"
                    >
                      🔗
                    </LinkIconButton>
                    <ExpandToggle
                      onClick={e => {
                        e.stopPropagation();
                        toggleCardExpanded(req.id);
                      }}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </ExpandToggle>
                  </MetaRight>
                )}
              </RequirementHeader>

              {shortDesc && (
                <RequirementDescription>
                  {shortDesc}
                  {shortDesc.length === 180 && '…'}
                </RequirementDescription>
              )}

              {hasLinks && isExpanded && (
                <LinksList
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  {linksForCard === undefined && (
                    <div>
                      {t(
                        'requirements.loadingLinks',
                        'Loading links…'
                      )}
                    </div>
                  )}

                  {linksForCard && linksForCard.length === 0 && (
                    <div>
                      {t(
                        'requirements.noLinksYet',
                        'No links yet for this requirement.'
                      )}
                    </div>
                  )}

                  {linksForCard &&
                    linksForCard.length > 0 &&
                    linksForCard.map(link => (
                      <LinkRow key={link.id || link.url}>
                        <span>
                          {link.name ||
                            link.displayName ||
                            'Linked item'}
                        </span>
                        <a
                          href={link.storageUrl || link.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={event => {
                            event.stopPropagation();
                          }}
                        >
                          {t('requirements.open', 'Open')}
                        </a>
                      </LinkRow>
                    ))}
                </LinksList>
              )}
            </RequirementMain>
          </RequirementCard>
        );
      },
      [
        cardLinks,
        expandedCards,
        requirementIdsWithLinks,
        t,
        toggleCardExpanded,
        handleEditRequirement
      ]
    );

    const getNodeCount = node => {
      const selfCount = node.requirements?.length || 0;
      const childCount = (node.children || []).reduce(
        (sum, child) => sum + getNodeCount(child),
        0
      );
      return selfCount + childCount;
    };

    const renderGroupNode = node => {
      const total = getNodeCount(node);
      const expanded = expandedGroups.has(node.key);

      const hasChildren =
        node.children && node.children.length > 0;

      return (
        <GroupContainer key={node.key}>
          <GroupHeader onClick={() => toggleGroupExpanded(node.key)}>
            <GroupHeaderLeft>
              <GroupToggleIcon>
                {expanded ? '▾' : '▸'}
              </GroupToggleIcon>
              <GroupTitle>{node.label}</GroupTitle>
            </GroupHeaderLeft>
            <GroupCount>{total}</GroupCount>
          </GroupHeader>

          {expanded && (
            <>
              {hasChildren ? (
                <GroupCategoryContainer>
                  {node.children.map(child => renderGroupNode(child))}
                </GroupCategoryContainer>
              ) : (
                <GroupRequirements>
                  {node.requirements.map(renderRequirementCard)}
                </GroupRequirements>
              )}
            </>
          )}
        </GroupContainer>
      );
    };

    return (
      <PageContainer>
        <FolderInputSection data-tour="link-folder">
          <StepLabel>Add a folder</StepLabel>
          <UrlInputGroup>
            <UrlInputWrap>
              <UrlInputIcon
                as="svg"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </UrlInputIcon>
              <FolderInput
                type="text"
                placeholder="Paste a SharePoint or Google Drive folder URL…"
                value={pendingFolderUrl}
                onChange={e => setPendingFolderUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && pendingFolderUrl.trim()) {
                    handleFolderUrlSubmit(pendingFolderUrl);
                    setPendingFolderUrl('');
                  }
                }}
              />
            </UrlInputWrap>
            <LinkFolderBtn
              onClick={() => {
                if (pendingFolderUrl.trim()) {
                  handleFolderUrlSubmit(pendingFolderUrl);
                  setPendingFolderUrl('');
                }
              }}
              disabled={!pendingFolderUrl.trim()}
            >
              + Add Folder
            </LinkFolderBtn>
          </UrlInputGroup>
          <InputHint>
            Supports Microsoft SharePoint, OneDrive, and Google Drive shared folders
          </InputHint>
        </FolderInputSection>
        <LinkedFoldersSection
          selectedProject={selectedProject}
          notifications={notifications}
          onOpenDrawingReview={onOpenDrawingReview}
          onAddFolder={(fn) => { linkedFoldersAddFolderRef.current = fn; }}
          onDeleted={(requirementId) => {
            setColumns(prev => prev.map(col => ({
              ...col,
              requirements: col.requirements.filter(r => r.id !== requirementId)
            })));
          }}
        />
          <ClassifierModal
            isOpen={classifierModalOpen}
            projectId={selectedProject?.id}
            requirementId={pendingRequirementId}
            linkId={pendingLinkId}
            onClose={async () => {
              setClassifierModalOpen(false);
              if (!classifierConfirmedRef.current && pendingRequirementId && pendingLinkId) {
                try {
                  const idToken = localStorage.getItem('id_token');
                  await axios.post(
                    `${process.env.REACT_APP_DELETE_REQUIREMENT_FILES_URL}/delete-requirement-files`,
                    {
                      projectId: selectedProject.id,
                      requirementId: pendingRequirementId,
                      linkId: pendingLinkId
                    },
                    { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
                  );
                } catch (err) {
                  console.error('[RequirementsPage] Failed to clean up unconfirmed upload:', err);
                } finally {
                  setPendingRequirementId('');
                  setPendingLinkId('');
                  classifierConfirmedRef.current = false; 
                }
              }
              classifierConfirmedRef.current = false; 
            }}
            jobId={classifierJobId}
            folderName={classifierFolderName}
            totalFiles={classifierTotalFiles}
            onConfirmed={(rows, jobId) => handleClassifierConfirmed(rows, jobId)}
            project={selectedProjectState || selectedProject}
            onConstructionTypeConfirmed={handleConstructionTypeConfirmed}
            onZoningDesignationConfirmed={handleZoningDesignationConfirmed}
          />
      </PageContainer>
    );
  }
);

export default RequirementsPage;
